import { Link } from "@tanstack/react-router";
import { Link as ReactRouterLink, useInRouterContext } from "react-router-dom";
import type { MouseEventHandler, ReactNode } from "react";

/**
 * A link to a path the TanStack route tree does not know about yet.
 *
 * TanStack types `<Link to>` against the generated route tree, so linking to a
 * screen that still lives in the legacy react-router tree is a type error — which
 * is the typed routing working correctly, not a nuisance. Those paths are served
 * by the catch-all in routes/$.tsx, so they are addressed through its splat
 * parameter rather than as routes in their own right.
 *
 * Inside the legacy tree, react-router must own the navigation so its current
 * location updates too. Its pushState also notifies TanStack's shared history.
 * Outside that tree, TanStack enters the catch-all and mounts react-router.
 *
 * This is migration scaffolding. Every time a screen becomes a real route, its
 * nav entry should move from LegacyLink to a plain typed <Link> — and when the
 * last one moves, this file is deleted. Treat a shrinking number of usages as the
 * progress metric.
 */
export const LegacyLink = ({
  to,
  className,
  title,
  children,
  exact,
  ...rest
}: {
  /** An absolute app path, e.g. "/settings". */
  to: string;
  className?: string;
  title?: string;
  exact?: boolean;
  children: ReactNode;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  "aria-label"?: string;
  "aria-current"?: "page";
}) => {
  const inLegacyRouter = useInRouterContext();
  if (inLegacyRouter) return <ReactRouterLink to={to} className={className} title={title} {...rest}>{children}</ReactRouterLink>;
  return <Link
    to="/$"
    params={{ _splat: to.replace(/^\//, "") }}
    className={className}
    title={title}
    activeOptions={{ exact: !!exact }}
    {...rest}
  >
    {children}
  </Link>;
};
